import { Eye, Share2 } from 'lucide-react';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatDate } from '@/lib/utils/date.utils';
import { initialsFromEmail, initialsFromName } from '@/lib/utils/user.utils';
import type { RecipientStatus, ShareRecipient } from '../api/recipients.api';

const STATUS_VARIANT: Record<RecipientStatus, 'green-solid' | 'expired' | 'revoked' | 'pending'> = {
  active: 'green-solid',
  expired: 'expired',
  revoked: 'revoked',
  pending: 'pending',
};

const STATUS_LABEL: Record<RecipientStatus, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  pending: 'Pending',
};

export interface RecipientsTableProps {
  recipients: ShareRecipient[];
  onRevoke: (recipientId: string) => void;
  onRegenerate: (recipientId: string) => void;
  /** Disables the row's own action while its mutation is in flight. */
  isRevoking?: (recipientId: string) => boolean;
  isRegenerating?: (recipientId: string) => boolean;
}

/**
 * "Shared Profile Recipients" table (MYN-41/54/57). Recipients are external
 * people (no MNR account) - the avatar uses initials derived from the
 * recipient's name (e.g. "Mark Johnson" -> "MJ"), which the sharer now always
 * provides for a newly-added recipient, matching the Figma
 * teams-and-permissions design. A legacy pre-migration row can still have an
 * empty `recipientName` (see `recipients.api.ts`'s doc comment) - that case
 * falls back to email-derived initials and drops the name line, i.e. the
 * original pre-name-field row treatment (mirrors `TeamMembersTable`'s
 * equivalent fallback for a nameless pending invite). Both actions are
 * disabled once a recipient is revoked (terminal - regenerating a revoked
 * recipient is rejected server-side; re-add them instead).
 */
export function RecipientsTable({
  recipients,
  onRevoke,
  onRegenerate,
  isRevoking,
  isRegenerating,
}: RecipientsTableProps) {
  const columns: DataTableColumn<ShareRecipient>[] = [
    {
      id: 'recipient',
      header: 'Recipient',
      cell: (r) => (
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green font-sans text-sm font-semibold text-white"
          >
            {r.recipientName ? initialsFromName(r.recipientName) : initialsFromEmail(r.recipientEmail)}
          </span>
          {r.recipientName ? (
            <div className="flex flex-col">
              <span className="font-sans text-sm font-semibold text-navy">{r.recipientName}</span>
              <span className="text-xs text-slate">{r.recipientEmail}</span>
            </div>
          ) : (
            // Legacy pre-migration row with no name on file: show just the
            // email, matching the original (pre-Task-5) single-line treatment.
            <span className="font-sans text-sm font-semibold text-navy">{r.recipientEmail}</span>
          )}
        </div>
      ),
    },
    {
      id: 'shared',
      header: 'Date Shared',
      cell: (r) => <span className="text-sm text-navy">{formatDate(r.createdAt)}</span>,
    },
    {
      id: 'viewed',
      header: 'Last Viewed',
      cell: (r) =>
        r.lastAccessedAt ? (
          <span className="inline-flex items-center gap-1.5 text-sm text-navy">
            <Eye size={14} className="text-green" aria-hidden="true" />
            {formatDate(r.lastAccessedAt)}
          </span>
        ) : (
          <span className="text-sm text-slate">Not yet viewed</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>,
    },
    {
      id: 'actions',
      header: 'Actions',
      className: 'text-right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-4 text-right">
          <Button
            type="button"
            variant="link"
            disabled={r.status === 'revoked' || (isRegenerating?.(r.id) ?? false)}
            onClick={() => onRegenerate(r.id)}
          >
            Resend Invite
          </Button>
          <Button
            type="button"
            variant="link"
            className="text-pink hover:text-pink-hover"
            disabled={r.status === 'revoked' || (isRevoking?.(r.id) ?? false)}
            onClick={() => onRevoke(r.id)}
          >
            Revoke Link
          </Button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      caption="Shared profile recipients"
      columns={columns}
      rows={recipients}
      rowKey={(r) => r.id}
      emptyState={{
        icon: <Share2 size={32} aria-hidden="true" />,
        title: 'No shared recipients yet',
        description: 'People you share your startup profile link with will appear here.',
      }}
    />
  );
}
